/**
 * EntityListPage - Lista e ricerca entità (Turbine/FAP/Iniettori)
 *
 * Features:
 * - Selezione tipo entità
 * - Lista entità dal database
 * - Ricerca per numero progressivo, qr_code, matricola
 * - Filtro per stato (in_accettazione, in_lavorazione, etc.)
 * - Click su entità → navigazione a dettaglio
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, QrCode, Wrench, Flame, Droplet, AlertCircle } from 'lucide-react';
import { useAuth } from '../../../components/auth/AuthProvider';

type EntityType = 'turbine' | 'fap' | 'iniettori';
type EntityStatus = 'in_accettazione' | 'in_lavorazione' | 'completata' | 'consegnata' | 'annullata';

interface Entity {
  id: string;
  numero_progressivo: number;
  qr_code?: string;
  marca_turbina?: string;
  modello_turbina?: string;
  matricola?: string;
  stato: EntityStatus;
  data_ingresso: string;
  cliente_nome?: string;
  mezzo_targa?: string;
}

const STATUS_LABELS: Record<EntityStatus, string> = {
  in_accettazione: '📥 In Accettazione',
  in_lavorazione: '🔧 In Lavorazione',
  completata: '✅ Completata',
  consegnata: '📦 Consegnata',
  annullata: '❌ Annullata'
};

const STATUS_COLORS: Record<EntityStatus, string> = {
  in_accettazione: 'bg-blue-900/30 text-blue-300 border-blue-700',
  in_lavorazione: 'bg-yellow-900/30 text-yellow-300 border-yellow-700',
  completata: 'bg-green-900/30 text-green-300 border-green-700',
  consegnata: 'bg-gray-700/30 text-gray-300 border-gray-600',
  annullata: 'bg-red-900/30 text-red-300 border-red-700'
};

function EntityListPage() {
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [entityType, setEntityType] = useState<EntityType>('turbine');
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filtri
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<EntityStatus | 'all'>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Fetch entità dal backend
  useEffect(() => {
    fetchEntities();
  }, [entityType]);

  const fetchEntities = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();

      // TODO: Per ora usiamo solo turbine, in futuro aggiungere FAP e Iniettori
      const endpoint = entityType === 'turbine'
        ? '/api/turbine/list'
        : `/api/${entityType}/list`;

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Errore ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Assumiamo che la risposta sia { success: true, data: [...] }
      setEntities(data.data || data.turbine || []);

    } catch (err: any) {
      console.error('Errore fetch entità:', err);
      setError(err.message || 'Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  // Filtra entità in base a ricerca e filtri
  const filteredEntities = entities.filter(entity => {
    // Filtro per stato
    if (filterStatus !== 'all' && entity.stato !== filterStatus) {
      return false;
    }

    // Filtro per ricerca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        entity.numero_progressivo.toString().includes(term) ||
        entity.qr_code?.toLowerCase().includes(term) ||
        entity.matricola?.toLowerCase().includes(term) ||
        entity.marca_turbina?.toLowerCase().includes(term) ||
        entity.modello_turbina?.toLowerCase().includes(term) ||
        entity.cliente_nome?.toLowerCase().includes(term) ||
        entity.mezzo_targa?.toLowerCase().includes(term)
      );
    }

    return true;
  });

  const handleEntityClick = (entity: Entity) => {
    navigate(`/apps/lavorazione/entity/${entity.id}`);
  };

  const handleQRScan = () => {
    navigate('/apps/lavorazione/scan');
  };

  return (
    <div className="min-h-screen bg-gray-900 p-6 space-y-6">
      {/* Header con selezione tipo */}
      <div className="bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
        <h2 className="text-2xl font-bold text-gray-100 mb-4">Seleziona Tipo Entità</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Turbine */}
          <button
            onClick={() => setEntityType('turbine')}
            className={`p-6 rounded-lg border-2 transition-all ${
              entityType === 'turbine'
                ? 'border-blue-500 bg-blue-500/20 shadow-lg shadow-blue-500/50'
                : 'border-gray-600 hover:border-blue-400 hover:bg-gray-700/50'
            }`}
          >
            <div className="flex flex-col items-center gap-3">
              <Wrench className={`w-12 h-12 ${entityType === 'turbine' ? 'text-blue-400' : 'text-gray-400'}`} />
              <span className={`text-lg font-semibold ${entityType === 'turbine' ? 'text-blue-300' : 'text-gray-300'}`}>
                Turbine
              </span>
              <span className="text-sm text-gray-400">
                {entities.length} in archivio
              </span>
            </div>
          </button>

          {/* FAP */}
          <button
            onClick={() => setEntityType('fap')}
            className={`p-6 rounded-lg border-2 transition-all opacity-50 cursor-not-allowed ${
              entityType === 'fap'
                ? 'border-orange-500 bg-orange-500/20 shadow-lg'
                : 'border-gray-700 bg-gray-800/50'
            }`}
            disabled
          >
            <div className="flex flex-col items-center gap-3">
              <Flame className={`w-12 h-12 ${entityType === 'fap' ? 'text-orange-400' : 'text-gray-600'}`} />
              <span className={`text-lg font-semibold ${entityType === 'fap' ? 'text-orange-300' : 'text-gray-500'}`}>
                FAP
              </span>
              <span className="text-sm text-gray-500">
                Prossimamente
              </span>
            </div>
          </button>

          {/* Iniettori */}
          <button
            onClick={() => setEntityType('iniettori')}
            className={`p-6 rounded-lg border-2 transition-all opacity-50 cursor-not-allowed ${
              entityType === 'iniettori'
                ? 'border-green-500 bg-green-500/20 shadow-lg'
                : 'border-gray-700 bg-gray-800/50'
            }`}
            disabled
          >
            <div className="flex flex-col items-center gap-3">
              <Droplet className={`w-12 h-12 ${entityType === 'iniettori' ? 'text-green-400' : 'text-gray-600'}`} />
              <span className={`text-lg font-semibold ${entityType === 'iniettori' ? 'text-green-300' : 'text-gray-500'}`}>
                Iniettori
              </span>
              <span className="text-sm text-gray-500">
                Prossimamente
              </span>
            </div>
          </button>
        </div>
      </div>

      {/* Barra ricerca e filtri */}
      <div className="bg-gray-800 rounded-lg shadow-xl p-4 border border-gray-700">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Ricerca */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cerca per numero, QR code, matricola, marca..."
              className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-600 text-gray-100 placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Pulsanti azione */}
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg border transition-colors inline-flex items-center gap-2 ${
                showFilters
                  ? 'bg-blue-500/20 border-blue-500 text-blue-300'
                  : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <Filter className="w-5 h-5" />
              Filtri
            </button>

            <button
              onClick={handleQRScan}
              className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors inline-flex items-center gap-2"
            >
              <QrCode className="w-5 h-5" />
              Scan QR
            </button>
          </div>
        </div>

        {/* Filtri estesi */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-700">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Filtra per stato:
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterStatus('all')}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors border ${
                  filterStatus === 'all'
                    ? 'bg-blue-500/20 border-blue-500 text-blue-300'
                    : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                }`}
              >
                Tutti
              </button>
              {Object.entries(STATUS_LABELS).map(([status, label]) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status as EntityStatus)}
                  className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${
                    filterStatus === status
                      ? STATUS_COLORS[status as EntityStatus]
                      : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lista entità */}
      <div className="bg-gray-800 rounded-lg shadow-xl border border-gray-700">
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
            <p className="mt-4 text-gray-300">Caricamento...</p>
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-400 font-semibold mb-2">Errore caricamento dati</p>
            <p className="text-gray-400 text-sm mb-4">{error}</p>
            <button
              onClick={fetchEntities}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
            >
              Riprova
            </button>
          </div>
        ) : filteredEntities.length === 0 ? (
          <div className="p-12 text-center">
            <Search className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">
              {searchTerm || filterStatus !== 'all'
                ? 'Nessun risultato trovato'
                : 'Nessuna entità in archivio'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {filteredEntities.map((entity) => (
              <div
                key={entity.id}
                onClick={() => handleEntityClick(entity)}
                className="p-4 hover:bg-gray-700/50 cursor-pointer transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <span className="text-lg font-bold text-gray-100">
                        #{entity.numero_progressivo}
                      </span>
                      {entity.qr_code && (
                        <span className="text-xs font-mono bg-gray-700 text-gray-300 px-2 py-1 rounded border border-gray-600">
                          QR: {entity.qr_code}
                        </span>
                      )}
                      <span className={`text-xs font-medium px-2 py-1 rounded-full border ${STATUS_COLORS[entity.stato]}`}>
                        {STATUS_LABELS[entity.stato]}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-300">
                      {entity.marca_turbina && (
                        <div>
                          <span className="font-medium text-gray-400">Marca:</span> {entity.marca_turbina}
                        </div>
                      )}
                      {entity.modello_turbina && (
                        <div>
                          <span className="font-medium text-gray-400">Modello:</span> {entity.modello_turbina}
                        </div>
                      )}
                      {entity.matricola && (
                        <div>
                          <span className="font-medium text-gray-400">Matricola:</span> {entity.matricola}
                        </div>
                      )}
                      {entity.cliente_nome && (
                        <div>
                          <span className="font-medium text-gray-400">Cliente:</span> {entity.cliente_nome}
                        </div>
                      )}
                      {entity.mezzo_targa && (
                        <div>
                          <span className="font-medium text-gray-400">Mezzo:</span> {entity.mezzo_targa}
                        </div>
                      )}
                      <div>
                        <span className="font-medium">Ingresso:</span> {new Date(entity.data_ingresso).toLocaleDateString('it-IT')}
                      </div>
                    </div>
                  </div>

                  <div className="ml-4">
                    <div className="text-2xl">
                      {entityType === 'turbine' && '⚙️'}
                      {entityType === 'fap' && '🔥'}
                      {entityType === 'iniettori' && '💉'}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer info */}
      {!loading && !error && filteredEntities.length > 0 && (
        <div className="text-center text-sm text-gray-500">
          Visualizzate {filteredEntities.length} di {entities.length} {entityType}
        </div>
      )}
    </div>
  );
}

export default EntityListPage;
